import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Banknote, Coins, AlertCircle, Check, Printer, X, ArrowLeft } from "lucide-react";

interface Order {
  id: string;
  order_number: number;
  total: number;
  customer_name?: string;
}

interface CashPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void; // 🔥 NUEVA PROP para volver al selector
  order: Order | null;
  onConfirmPayment: (orderId: string, cashReceived: number, changeReturned: number) => Promise<void>;
  isLoading?: boolean;
}

const QUICK_AMOUNTS = [5, 10, 20, 50, 100];

export default function CashPaymentModal({
  isOpen,
  onClose,
  onBack,
  order,
  onConfirmPayment,
  isLoading = false,
}: CashPaymentModalProps) {
  const [cashReceived, setCashReceived] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [finalChange, setFinalChange] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const total = order?.total || 0;
  const receivedAmount = parseFloat(cashReceived) || 0;
  const change = receivedAmount - total;
  const isValidPayment = receivedAmount >= total;
  const missingAmount = total - receivedAmount;

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setCashReceived("");
      setPaymentComplete(false);
      setFinalChange(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleQuickAmount = (amount: number) => {
    setCashReceived(amount.toFixed(2));
  };

  const handleExactAmount = () => {
    setCashReceived(total.toFixed(2));
  };

  const handleConfirm = async () => {
    if (!order || !isValidPayment) return;
    
    setIsProcessing(true);
    try {
      await onConfirmPayment(order.id, receivedAmount, change);
      // Show success screen instead of closing
      setFinalChange(change);
      setPaymentComplete(true);
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrintAndClose = () => {
    // Dispatch event for Admin.tsx to handle printing
    if (order) {
      window.dispatchEvent(new CustomEvent('print-receipt', { detail: order.id }));
    }
    handleCloseDirect();
  };

  const handleCloseDirect = () => {
    setPaymentComplete(false);
    setCashReceived("");
    setFinalChange(0);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setCashReceived(value);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCloseDirect()}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        {paymentComplete ? (
          // ✅ SUCCESS SCREEN
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Cobro realizado</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-6 text-center">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-12 w-12 text-green-600" />
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-green-700">¡Cobro realizado!</h3>
                <p className="text-muted-foreground">Pedido #{order?.order_number}</p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-300">
                <p className="text-sm text-green-700 mb-1">CAMBIO A DEVOLVER</p>
                <p className="text-4xl font-bold text-green-600">{finalChange.toFixed(2)}€</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3 pt-4">
                <Button
                  onClick={handlePrintAndClose}
                  className="h-14 text-lg font-bold bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Printer className="h-5 w-5 mr-2" />
                  Imprimir
                </Button>
                <Button
                  onClick={handleCloseDirect}
                  variant="outline"
                  className="h-14 text-lg font-semibold border-2"
                >
                  <X className="h-5 w-5 mr-2" />
                  No Imprimir
                </Button>
              </div>
            </div>
          </>
        ) : (
          // 💰 PAYMENT FORM
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                {onBack && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground -ml-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Volver
                  </Button>
                )}
                <DialogTitle className="flex items-center gap-2 text-xl flex-1 justify-center">
                  <Banknote className="h-6 w-6 text-green-600" />
                  Cobro en Efectivo
                </DialogTitle>
                {/* Spacer to balance the back button */}
                {onBack && <div className="w-16" />}
              </div>
              <p className="text-muted-foreground text-sm text-center">
                Pedido #{order?.order_number}
                {order?.customer_name && ` • ${order.customer_name}`}
              </p>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Total to pay */}
              <div className="bg-primary/10 rounded-xl p-6 text-center border-2 border-primary/20">
                <p className="text-sm text-muted-foreground mb-1">TOTAL A PAGAR</p>
                <p className="text-4xl font-bold text-primary">
                  {total.toFixed(2)}€
                </p>
              </div>

              {/* Cash received input */}
              <div className="space-y-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Coins className="h-4 w-4" />
                  Entregado por cliente
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground">
                    €
                  </span>
                  <Input
                    ref={inputRef}
                    type="text"
                    inputMode="decimal"
                    value={cashReceived}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="text-3xl font-bold h-16 pl-12 pr-4 text-right bg-background border-2 focus:border-primary"
                  />
                </div>
              </div>

              {/* Quick amount buttons */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Dinero rápido:</p>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExactAmount}
                    className="h-12 text-base font-semibold border-2 hover:bg-green-50 hover:border-green-500 hover:text-green-700"
                  >
                    ✓ Exacto
                  </Button>
                  {QUICK_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant="outline"
                      onClick={() => handleQuickAmount(amount)}
                      disabled={amount < total}
                      className={`h-12 text-base font-semibold border-2 ${
                        amount < total 
                          ? "opacity-40" 
                          : "hover:bg-primary/10 hover:border-primary"
                      }`}
                    >
                      {amount}€
                    </Button>
                  ))}
                </div>
              </div>

              {/* Change calculation */}
              <div className={`rounded-xl p-4 text-center border-2 transition-colors ${
                !cashReceived 
                  ? "bg-muted/50 border-muted" 
                  : isValidPayment 
                    ? "bg-green-50 border-green-500 dark:bg-green-950/30" 
                    : "bg-red-50 border-red-400 dark:bg-red-950/30"
              }`}>
                {!cashReceived ? (
                  <p className="text-muted-foreground">
                    Introduce el importe entregado
                  </p>
                ) : isValidPayment ? (
                  <>
                    <p className="text-sm text-green-700 dark:text-green-400 mb-1">
                      CAMBIO A DEVOLVER
                    </p>
                    <p className="text-3xl font-bold text-green-600">
                      {change.toFixed(2)}€
                    </p>
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">
                      Falta {missingAmount.toFixed(2)}€
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Confirm button - now just "Cobrar" */}
            <Button
              onClick={handleConfirm}
              disabled={!isValidPayment || isProcessing || isLoading}
              className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {isProcessing || isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Procesando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Banknote className="h-5 w-5" />
                  💸 Cobrar
                </span>
              )}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
